<script setup>
import { useConfigStore } from '@core/stores/config'
import { AppContentLayoutNav } from '@layouts/enums'
import { useUserStore } from '~/stores/user'

/**
 * This app only has a vertical navigation. The horizontal layout came from the
 * starter template and was never built out — its nav list was still the template
 * stub ("Home" / "Second page") with no role/ACL awareness, so anyone whose
 * `appContentLayoutNav` cookie said "horizontal" lost their entire menu and saw
 * two dead links instead. Nothing in the UI offers that choice, so the only way in
 * was a stale cookie from an old build — which is exactly how it bit us.
 *
 * The layout is therefore fixed to vertical rather than switched on config.
 */
const DefaultLayoutWithVerticalNav = defineAsyncComponent(() => import('./components/DefaultLayoutWithVerticalNav.vue'))
const configStore = useConfigStore()
const userStore = useUserStore()

// Heal any stale cookie still holding "horizontal". Without this the value keeps
// feeding `layout-nav-type-*` / `horizontal-nav-*` classes into the layout wrapper
// even though we always render the vertical component.
if (configStore.appContentLayoutNav !== AppContentLayoutNav.Vertical) {
  configStore.appContentLayoutNav = AppContentLayoutNav.Vertical
}

const isImpersonating = ref(false)

onMounted(() => {
  isImpersonating.value = localStorage.getItem('impersonating') === 'true'
})

const exitImpersonation = () => {
  const superSession = localStorage.getItem('super_session')
  if (superSession) {
    const { profile, token } = JSON.parse(superSession)
    localStorage.removeItem('super_session')
    localStorage.removeItem('impersonating')
    isImpersonating.value = false
    userStore.login(profile, token)
    window.location.href = '/super/businesses'
  } else {
    localStorage.removeItem('impersonating')
    isImpersonating.value = false
    userStore.logout()
  }
}

// switchToVerticalNavOnLtOverlayNavBreakpoint() removed along with the horizontal
// layout — it only existed to flip horizontal back to vertical on small screens.

const { layoutAttrs, injectSkinClasses } = useSkins()

injectSkinClasses()

//redirector()
</script>

<template>
  <div>
    <VAlert
      v-if="isImpersonating"
      color="warning"
      variant="flat"
      density="compact"
      class="impersonation-banner"
    >
      <div class="d-flex align-center justify-space-between w-100">
        <span>
          <VIcon icon="mdi mdi-account-switch" class="me-2"/>
          You are viewing as <strong>{{ userStore.profile?.firstName }} {{ userStore.profile?.lastName }}</strong>
        </span>
        <VBtn size="small" variant="flat" color="white" class="text-warning" @click="exitImpersonation">
          Exit & Return to Super Admin
        </VBtn>
      </div>
    </VAlert>
    <DefaultLayoutWithVerticalNav v-bind="layoutAttrs">
      <slot/>
    </DefaultLayoutWithVerticalNav>
  </div>
</template>

<style lang="scss">
// As we are using `layouts` plugin we need its styles to be imported
@use "@layouts/styles/default-layout";

.impersonation-banner {
  position: sticky;
  top: 0;
  z-index: 9999;
  border-radius: 0;
}
</style>
